import { ProjectionScenario } from '../data/rateProjections';

import { projectMonthlyBills } from './projectMonthlyBills';
import { BillPoint, ProjectionInput } from './types';

const ALL_SCENARIOS: ProjectionScenario[] = ['AEO', 'BLEND', 'RECENT'];

export type ScenarioRange = {
  /** Per projected year: the cheapest and most expensive scenario's bill */
  byYear: Record<number, { highUsd: number; lowUsd: number }>;
  /** The scenarios at the extremes of the final year, for the range caption */
  high: { scenario: ProjectionScenario; usd: number };
  low: { scenario: ProjectionScenario; usd: number };
};

type ScenarioSeries = { points: BillPoint[]; scenario: ProjectionScenario };

const hasPoints = (entry: { points?: BillPoint[]; scenario: ProjectionScenario }): entry is ScenarioSeries =>
  entry.points !== undefined && entry.points.length > 0;

// The spread across all scenarios — the honest "we don't know which pace"
// band rendered as the fan's shaded edges and the spread line under the totals
export const scenarioRange = (input: Omit<ProjectionInput, 'homeSqft'>): ScenarioRange | undefined => {
  const series = ALL_SCENARIOS.map((scenario) => ({
    points: projectMonthlyBills(input, undefined, scenario)?.filter(({ isProjected }) => isProjected),
    scenario,
  })).filter(hasPoints);
  if (series.length < ALL_SCENARIOS.length) {
    return undefined;
  }

  const byYear: Record<number, { highUsd: number; lowUsd: number }> = {};
  series.forEach(({ points }) =>
    points.forEach(({ monthlyBillUsd, year }) => {
      const entry = byYear[year] ?? { highUsd: monthlyBillUsd, lowUsd: monthlyBillUsd };
      byYear[year] = {
        highUsd: Math.max(entry.highUsd, monthlyBillUsd),
        lowUsd: Math.min(entry.lowUsd, monthlyBillUsd),
      };
    })
  );

  const finals = series.map(({ points, scenario }) => ({
    scenario,
    usd: points[points.length - 1].monthlyBillUsd,
  }));
  const high = finals.reduce((max, entry) => (entry.usd > max.usd ? entry : max));
  const low = finals.reduce((min, entry) => (entry.usd < min.usd ? entry : min));

  return { byYear, high, low };
};
